import React from "react"
import { Field as FieldPrimitive } from "@base-ui/react/field"
import { cn } from "../utils"

// =============================================================================
// Styles
// =============================================================================
const labelStyles = [
  "font-sans text-sm leading-6 font-medium", // typography
  "text-gray-900" // greying is owned by the field or the item, which see the control's state
]

// =============================================================================
// Props
// =============================================================================
export type FieldLabelProps = {
  /** Label text */
  children?: React.ReactNode
  /** Additional CSS classes for layout */
  className?: string
}

// =============================================================================
// Component
// =============================================================================
/**
 * The label for the control inside a `Field`.
 *
 * There is no `htmlFor` to pass: the label takes the id the control registered
 * with the surrounding `Field`, which is what gives the control its accessible
 * name and makes clicking the label operate it. The text also greys out with
 * the control, whether `disabled` was set on the `Field` or on the control.
 *
 * Needs a control that registers itself with the field — anything built on
 * Base UI does. Pair it with a raw `<input>` and the `for` points at an id
 * nothing owns, leaving the control with no accessible name and no warning.
 */
export const FieldLabel = React.forwardRef<HTMLLabelElement, FieldLabelProps>(
  function FieldLabel({ children, className }, ref) {
    return (
      <FieldPrimitive.Label
        ref={ref}
        data-slot="field-label"
        className={cn(labelStyles, className)}
      >
        {children}
      </FieldPrimitive.Label>
    )
  }
)

FieldLabel.displayName = "Field.Label"
